import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Box, Paper, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Node } from './Node';
import { Links } from './Links';
import { LegLabel } from './LegLabel';
import { DebugOverlay, useDebugMetrics } from './DebugOverlay';
import { NodeData, NavigationUtils, LayoutNode, LegLabelData } from '../../../constants/interfaces';
import { SortBy } from '../../../constants/enums';
import { 
  detectDataChanges, 
  detectExpandChange, 
  shouldRecalculateLayout
} from './utils/dataOptimizations';
import { 
  LayoutCache,
  createLayoutFingerprint,
  canUseCachedLayout,
  createLayoutCache,
  cacheToLayoutResult
} from './utils/layoutCache';
import {
  canUseIncrementalLayout,
  performIncrementalLayout
} from './utils/incrementalLayout';
import {
  useViewport,
  useVirtualizedNodes,
  getVisibleLinks,
  getVirtualizationStats
} from './utils/virtualization';
import {
  createDynamicLayout,
  createTiledLayout,
  toPixelValue
} from './layout';
// Simple mobile detection
const isMobileLayout = (containerWidth: number): boolean => {
  return containerWidth < 768; // Mobile breakpoint
};
// Hook to get actual container width with debouncing
const useContainerWidth = (containerRef: React.RefObject<HTMLElement>) => {
  const [containerWidth, setContainerWidth] = useState<number>(800);
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const updateWidth = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (containerRef.current) {
          const actualWidth = containerRef.current.clientWidth;
          // Only update if width actually changed significantly
          if (Math.abs(actualWidth - containerWidth) > 10) {
            setContainerWidth(actualWidth);
          }
        }
      }, 100); // Debounce by 100ms
    };
    // Initial measurement (immediate)
    if (containerRef.current) {
      const initialWidth = containerRef.current.clientWidth;
      setContainerWidth(initialWidth);
    }
    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    window.addEventListener('resize', updateWidth);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
    // Mount-once setup: containerRef is a stable ref and containerWidth is only
    // read inside a debounced threshold check; re-subscribing the observer on
    // every width change is undesirable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return containerWidth;
};
import { applyCentralizedSorting } from './utils/sortUtils';
// Pure helper to find a node in the tree by id (module scope: stable identity)
const findNodeInTree = (node: NodeData, nodeId: string): NodeData | null => {
  if (node.id === nodeId) return node;
  if (node.children) {
    for (const child of node.children) {
      const found = findNodeInTree(child, nodeId);
      if (found) return found;
    }
  }
  return null;
};
const ScrollableArea = styled(Box)<{ width?: number | string; height?: number | string; isFullscreen?: boolean }>(({ width = 1200, height = 800, isFullscreen = false }) => ({
  width: isFullscreen ? '100vw' : (typeof width === 'number' ? `${width}px` : width),
  height: isFullscreen ? '100vh' : (typeof height === 'number' ? `${height}px` : height),
  overflow: 'auto', // Enable scrolling
  position: 'relative',
}));
const TreeContainer = styled(Paper)<{ width?: number | string; height?: number | string; isFullscreen?: boolean }>(({ theme, width = 1200, height = 800, isFullscreen = false }) => ({
  width: isFullscreen ? '100vw' : (typeof width === 'number' ? `${width}px` : width),
  height: isFullscreen ? '100vh' : (typeof height === 'number' ? `${height}px` : height),
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.default,
  border: isFullscreen ? 'none' : `1px solid ${theme.palette.divider}`,
  borderRadius: isFullscreen ? 0 : theme.shape.borderRadius,
}));
const TreeCanvas = styled(Box)(({ theme: _theme }) => ({
  position: 'relative',
  minWidth: '100%',
  minHeight: '100%',
  transition: 'transform 0.6s cubic-bezier(0.4, 0.0, 0.2, 1), opacity 0.3s ease-in-out',
  transformOrigin: 'center center',
}));
export interface Customer {
  unicity: string;
  id: { unicity: string };
  humanName: {
    firstName: string;
    lastName: string;
    fullName: string;
    [key: string]: unknown; // Allow localized names like fullName@fr, fullName@en, etc.
  };
  email?: string;
  sponsor?: {
    unicity: string;
    id: { unicity: string };
  };
  profilePicture?: {
    href: string;
    sizes?: Array<{
      size: string;
      media: string;
    }>;
  };
  metricsProfile?: {
    rankShort?: string;
    [key: string]: unknown;
  };
  status?: string;
  legLevel?: number; // Custom leg level (1=Kiran, 2=Frank, 3=others)
  [key: string]: unknown; // Allow any additional properties from API
}
export interface TreeApiResponse {
  customer: Customer;
  treeDepth: number;
}
export interface GenealogyTreeViewProps {
  data: TreeApiResponse[];
  width?: number | string;
  height?: number | string;
  onNodeClick?: (data: NodeData) => void;
  onNodeExpand?: (data: NodeData) => void;
  rootCustomerId?: string;
  selectedNodeId?: string;
  debug?: boolean;
  virtualization?: boolean; // Enable virtual scrolling for massive performance gains
  filterMode?: boolean; // Enable tiled layout centered in view area
  onFilterModeChange?: (filterMode: boolean) => void; // Callback when component wants to change filter mode
  expandAll?: boolean; // Optional: External trigger to expand all nodes (internal expand/collapse button is available)
  onExpandAllComplete?: (expandedCount: number) => void; // Optional: Callback when expand all is complete
  autoFocus?: boolean; // Auto focus the component for keyboard navigation
  sortBy?: SortBy; // Sort order for children
}
// Transform API data to our internal node format
const transformApiData = (apiData: TreeApiResponse[], debug: boolean = false): NodeData[] => {
  if (debug) console.log('🔄 transformApiData called with:', {
    dataLength: apiData.length,
    firstFewItems: apiData.slice(0, 3).map(item => ({
      unicity: item.customer.unicity,
      name: `${item.customer.humanName.firstName} ${item.customer.humanName.lastName}`,
      sponsor: item.customer.sponsor?.unicity || 'no sponsor'
    }))
  });
  const nodeMap = new Map<string, NodeData>();  
  // First pass: create all nodes
  apiData.forEach((item) => {
    const { customer, treeDepth } = item;
    const node: NodeData = {
      id: customer.unicity,
      firstName: customer.humanName.firstName,
      lastName: customer.humanName.lastName,
      fullName: customer.humanName.fullName,
      unicity: customer.unicity,
      email: customer.email,
      profilePicture: customer.profilePicture?.href,
      rank: customer.metricsProfile?.rankShort,
      treeDepth,
      sponsor: customer.sponsor ? { unicity: customer.sponsor.unicity } : undefined,
      children: [],
      status: customer.status,
      metricsProfile: customer.metricsProfile,
      legLevel: customer.legLevel, // Include the custom legLevel from customer data
    };
    nodeMap.set(customer.unicity, node);
  });
  // Second pass: build relationships
  const rootNodes: NodeData[] = [];
  nodeMap.forEach((node) => {
    if (node.sponsor) {
      const parentNode = nodeMap.get(node.sponsor.unicity);
      if (parentNode) {
        parentNode.children = parentNode.children || [];
        parentNode.children.push(node);
      } else {
        // Parent not found in filtered data - treat as root
        if (debug) console.log('⚠️ Parent not found for node:', {
          nodeName: `${node.firstName} ${node.lastName}`,
          nodeId: node.id,
          expectedParentId: node.sponsor.unicity,
          treatingAsRoot: true
        });
        rootNodes.push(node);
      }
    } else {
      rootNodes.push(node);
    }
  });
  if (debug) console.log('🔄 transformApiData result:', {
    totalRootNodes: rootNodes.length,
    rootNodeNames: rootNodes.map(n => `${n.firstName} ${n.lastName}`)
  });
  // Debug: Log tree structure if in debug mode
  if (debug) {
    console.log('🌳 Tree structure analysis:');
    const analyzeTree = (node: NodeData, level: number = 0) => {
      const indent = '  '.repeat(level);
      console.log(`${indent}${node.firstName} ${node.lastName} (depth: ${node.treeDepth}, children: ${node.children?.length || 0})`);
      if (node.children && level < 3) { // Only show first 3 levels to avoid spam
        node.children.slice(0, 3).forEach(child => analyzeTree(child, level + 1));
        if (node.children.length > 3) {
          console.log(`${indent}  ... and ${node.children.length - 3} more children`);
        }
      }
    };
    rootNodes.forEach(root => analyzeTree(root));
  }
  return rootNodes;
};
// Keyboard navigation utilities
const createNavigationUtils = (apiData: TreeApiResponse[], debug: boolean = false, transformedData?: NodeData[]): NavigationUtils => {
  const findNodeById = (nodeId: string, nodes: NodeData[]): NodeData | null => {
    for (const node of nodes) {
      if (node.id === nodeId) return node;
      if (node.children) {
        const found = findNodeById(nodeId, node.children);
        if (found) return found;
      }
    }
    return null;
  };
  const findParentNode = (nodeId: string, nodes: NodeData[], parent: NodeData | null = null): NodeData | null => {
    for (const node of nodes) {
      if (node.id === nodeId) {
        if (debug) {
          console.log('🔍 findParentNode result:', {
            searchingFor: nodeId,
            foundParent: parent ? `${parent.firstName} ${parent.lastName} (depth: ${parent.treeDepth})` : 'null',
            parentId: parent?.id || 'null'
          });
        }
        return parent;
      }
      if (node.children) {
        const found = findParentNode(nodeId, node.children, node);
        if (found) return found;
      }
    }
    return null;
  };
  // Helper function to sort children - now accepts sortBy as parameter
  const getSortedChildren = (node: NodeData, isRootChildren: boolean = false, currentSortBy: SortBy = SortBy.Alphabetical): NodeData[] => {
    if (!node.children) {
      if (debug) console.log('🚫 getSortedChildren: No children for node', `${node.firstName} ${node.lastName}`);
      return [];
    }
    if (debug) {
      console.log('🔧 getSortedChildren USING DYNAMIC sortBy:', {
        parentNode: `${node.firstName} ${node.lastName}`,
        parentTreeDepth: node.treeDepth,
        isRootChildren,
        currentSortBy,
        currentSortByType: typeof currentSortBy,
        childrenCount: node.children.length
      });
    }
    // Use centralized sorting function with the provided sortBy
    return applyCentralizedSorting(node.children, isRootChildren, currentSortBy, apiData, debug);
  };
  // Get the internal transformed data for navigation - fixes data format mismatch
  const internalTransformedData = transformedData || transformApiData(apiData, debug);
  return {
    findParent: (nodeId: string, _externalTransformedData?: NodeData[]) => {
      // Use internal transformed data for consistency - fixes level 3+ navigation
      const dataToUse = _externalTransformedData || internalTransformedData;
      // Return the actual parent - if it's null (direct child of root), stay null
      // This prevents children of root from navigating back to root with left arrow
      return findParentNode(nodeId, dataToUse);
    },
    findSiblings: (nodeId: string, _externalTransformedData?: NodeData[], currentSortBy: SortBy = SortBy.Alphabetical) => {
      const dataToUse = _externalTransformedData || internalTransformedData;
      const parent = findParentNode(nodeId, dataToUse);
      if (!parent || !parent.children) return [];
      // Check if this is root's children (parent is root node with treeDepth 0)
      const isRootChildren = parent.treeDepth === 0;
      return getSortedChildren(parent, isRootChildren, currentSortBy);
    },
    findFirstChild: (nodeId: string, _externalTransformedData?: NodeData[], expandedNodes?: Set<string>, currentSortBy: SortBy = SortBy.Alphabetical) => {
      const dataToUse = _externalTransformedData || internalTransformedData;
      const node = findNodeById(nodeId, dataToUse);
      if (!node || !node.children || node.children.length === 0) return null;
      // Use external expandedNodes if provided, otherwise this function can't work properly
      if (expandedNodes && !expandedNodes.has(nodeId)) return null;
      // Check if this is root node
      const isRootChildren = node.treeDepth === 0;
      const sortedChildren = getSortedChildren(node, isRootChildren, currentSortBy);
      return sortedChildren[0];
    },
    findNextSibling: (nodeId: string, _externalTransformedData?: NodeData[], currentSortBy: SortBy = SortBy.Alphabetical) => {
      if (debug) console.log('🔽 findNextSibling called with DYNAMIC sortBy:', currentSortBy);
      const dataToUse = _externalTransformedData || internalTransformedData;
      const parent = findParentNode(nodeId, dataToUse);
      if (!parent || !parent.children) {
        if (debug) console.log('🚫 findNextSibling: No parent or children found for', nodeId);
        return null;
      }
      if (debug) {
        console.log('🔍 findNextSibling parent found:', {
          parentId: parent.id,
          parentName: `${parent.firstName} ${parent.lastName}`,
          parentTreeDepth: parent.treeDepth,
          parentChildrenCount: parent.children.length
        });
      }
      // Check if this is root's children
      const isRootChildren = parent.treeDepth === 0;
      if (debug) {
        console.log('🔧 findNextSibling calling getSortedChildren with DYNAMIC sortBy:', {
          isRootChildren,
          currentSortBy: currentSortBy,
          currentSortByType: typeof currentSortBy,
          parentTreeDepth: parent.treeDepth,
          navigationUsingSortBy: currentSortBy
        });
      }
      const sortedChildren = getSortedChildren(parent, isRootChildren, currentSortBy);
      const currentIndex = sortedChildren.findIndex(child => child.id === nodeId);
      if (debug) {
        console.log('🔍 findNextSibling navigation data with DYNAMIC sortBy:', {
          nodeId,
          parentId: parent.id,
          parentTreeDepth: parent.treeDepth,
          isRootChildren,
          currentSortBy,
          totalChildren: sortedChildren.length,
          currentIndex,
          childrenNames: sortedChildren.map(c => `${c.firstName} ${c.lastName}`),
          currentChild: currentIndex >= 0 ? `${sortedChildren[currentIndex]?.firstName} ${sortedChildren[currentIndex]?.lastName}` : 'not found',
          nextChild: currentIndex >= 0 && currentIndex < sortedChildren.length - 1 ? `${sortedChildren[currentIndex + 1]?.firstName} ${sortedChildren[currentIndex + 1]?.lastName}` : 'none'
        });
      }
      if (currentIndex === -1) {
        if (debug) console.log('🚫 findNextSibling: Current node not found in sorted children list!');
        return null;
      }
      if (currentIndex === sortedChildren.length - 1) {
        if (debug) console.log('🚫 findNextSibling: At end of children list');
        return null;
      }
      const nextSibling = sortedChildren[currentIndex + 1];
      if (debug) console.log('✅ findNextSibling: Found next sibling', `${nextSibling.firstName} ${nextSibling.lastName}`);
      return nextSibling;
    },
    findPreviousSibling: (nodeId: string, _externalTransformedData?: NodeData[], currentSortBy: SortBy = SortBy.Alphabetical) => {
      if (debug) console.log('🔼 findPreviousSibling called with DYNAMIC sortBy:', currentSortBy);
      const dataToUse = _externalTransformedData || internalTransformedData;
      const parent = findParentNode(nodeId, dataToUse);
      if (!parent || !parent.children) {
        if (debug) console.log('🚫 findPreviousSibling: No parent or children found for', nodeId);
        return null;
      }
      if (debug) {
        console.log('🔍 findPreviousSibling parent found:', {
          parentId: parent.id,
          parentName: `${parent.firstName} ${parent.lastName}`,
          parentTreeDepth: parent.treeDepth,
          parentChildrenCount: parent.children.length
        });
      }
      // Check if this is root's children
      const isRootChildren = parent.treeDepth === 0;
      if (debug) {
        console.log('🔧 findPreviousSibling calling getSortedChildren with DYNAMIC sortBy:', {
          isRootChildren,
          currentSortBy: currentSortBy,
          currentSortByType: typeof currentSortBy,
          parentTreeDepth: parent.treeDepth,
          navigationUsingSortBy: currentSortBy
        });
      }
      const sortedChildren = getSortedChildren(parent, isRootChildren, currentSortBy);
      const currentIndex = sortedChildren.findIndex(child => child.id === nodeId);
      if (debug) {
        console.log('🔍 findPreviousSibling navigation data with DYNAMIC sortBy:', {
          nodeId,
          parentId: parent.id,
          parentTreeDepth: parent.treeDepth,
          isRootChildren,
          currentSortBy,
          totalChildren: sortedChildren.length,
          currentIndex,
          childrenNames: sortedChildren.map(c => `${c.firstName} ${c.lastName}`),
          currentChild: currentIndex >= 0 ? `${sortedChildren[currentIndex]?.firstName} ${sortedChildren[currentIndex]?.lastName}` : 'not found',
          previousChild: currentIndex > 0 ? `${sortedChildren[currentIndex - 1]?.firstName} ${sortedChildren[currentIndex - 1]?.lastName}` : 'none'
        });
      }
      if (currentIndex === -1) {
        if (debug) console.log('🚫 findPreviousSibling: Current node not found in sorted children list!');
        return null;
      }
      if (currentIndex === 0) {
        if (debug) console.log('🚫 findPreviousSibling: At beginning of children list');
        return null;
      }
      const previousSibling = sortedChildren[currentIndex - 1];
      if (debug) console.log('✅ findPreviousSibling: Found previous sibling', `${previousSibling.firstName} ${previousSibling.lastName}`);
      return previousSibling;
    },
    findFirstChildOfRoot: (_externalTransformedData?: NodeData[], currentSortBy: SortBy = SortBy.Alphabetical) => {
      const dataToUse = _externalTransformedData || internalTransformedData;
      if (dataToUse.length === 0) return null;
      const root = dataToUse[0];
      if (!root.children || root.children.length === 0) return null;
      const sortedChildren = getSortedChildren(root, true, currentSortBy); // Root's children
      return sortedChildren[0];
    }
  };
};
export const GenealogyTreeView: React.FC<GenealogyTreeViewProps> = ({
  data,
  width = 1200,
  height = 800,
  onNodeClick,
  onNodeExpand,
  rootCustomerId,
  selectedNodeId: externalSelectedNodeId,
  debug = false,
  virtualization = true, // Enable by default for performance
  filterMode = false, // Enable tiled layout when true
  onFilterModeChange,
  expandAll,
  onExpandAllComplete,
  autoFocus = false, // Auto focus the component for keyboard navigation
  sortBy, // Sort order for children
}) => {
  // Internal state for selected node (can be controlled externally via selectedNodeId prop)
  const [internalSelectedNodeId, setInternalSelectedNodeId] = useState<string | undefined>(undefined);
  // Use external selectedNodeId if provided, otherwise use internal state
  const selectedNodeId = externalSelectedNodeId !== undefined ? externalSelectedNodeId : internalSelectedNodeId;
  // Simple state to track when we need to center a node after switching from filter mode
  const [nodeToCenter, setNodeToCenter] = useState<string | null>(null);
  const centeringTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Local focus state - tracks when this component has focus for keyboard navigation
  const [isTreeFocused, setIsTreeFocused] = useState<boolean>(false);
  // Handle viewport reset when switching modes (without animation)
  useEffect(() => {
      // Reset viewport scroll position when switching modes
      if (scrollableAreaRef.current) {
        scrollableAreaRef.current.scrollTo({
          left: 0,
          top: 0,
          behavior: 'smooth'
        });
        if (debug) {
          console.log('📺 Reset viewport to (0, 0) due to filterMode change');
        }
      }
  }, [filterMode, debug]);
  // Initialize debug metrics if debug mode is enabled
  const {
    metrics: debugMetrics,
    updateMetrics: updateDebugMetrics,
  } = useDebugMetrics();
  // Viewport tracking for virtualization
  const containerRef = useRef<HTMLDivElement>(null); // For fullscreen functionality
  const scrollableAreaRef = useRef<HTMLDivElement>(null); // For viewport tracking
  const treeContainerRef = useRef<HTMLDivElement>(null);
  const viewport = useViewport(scrollableAreaRef);
  // Get actual container width dynamically (only for mobile detection)
  const actualContainerWidth = useContainerWidth(scrollableAreaRef);
  // Use static width for layout calculations to prevent infinite re-renders
  const layoutWidth = toPixelValue(width);
  // Navigation utilities for keyboard support - now using dynamic sortBy
  const navigationUtils = useMemo(() => {
    const transformed = transformApiData(data, debug);
    return createNavigationUtils(data, debug, transformed); // Remove sortBy from creation
  }, [data, debug]); // Remove sortBy from dependencies
  // Find root customer dynamically from the data
  const rootCustomer = useMemo(() => {
    if (rootCustomerId) {
      return data.find(item => item.customer.unicity === rootCustomerId);
    }
    // Find root by treeDepth = 0 or first customer if no depth specified
    return data.find(item => item.treeDepth === 0) || data[0];
  }, [data, rootCustomerId]);
  // Initialize with no nodes expanded - start completely collapsed
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(() => {
    // Start with empty Set so expand/collapse button shows ⊞ (expand all) initially
    return new Set<string>();
  });
  // Fullscreen state tracking
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  // Optimization: Track previous data and expand state for change detection
  const previousDataRef = useRef<TreeApiResponse[]>([]);
  const previousExpandedRef = useRef<Set<string>>(new Set());
  const layoutCacheRef = useRef<LayoutCache | null>(null);
  // Optimized transform and layout calculation with incremental updates
  const { transformedData, layout } = useMemo(() => {
    if (debug) console.log('🔄 Layout calculation started');
    // const _layoutStartTime = debug ? performance.now() : 0;
    // No special handling needed for layout optimization bypass
    // Detect what changed in the data
    const dataChanges = detectDataChanges(previousDataRef.current, data);
    const expandChange = detectExpandChange(previousExpandedRef.current, expandedNodes);
    // Check if we can use incremental layout (biggest performance win!)
    const canUseIncremental = canUseIncrementalLayout(dataChanges, expandChange);
    // Check if we need to recalculate layout
    const layoutDecision = shouldRecalculateLayout(dataChanges, expandChange);
    // Create current state fingerprint - use viewport dimensions in fullscreen mode
    const effectiveWidth = isFullscreen ? window.innerWidth : layoutWidth;
    const effectiveHeight = isFullscreen ? window.innerHeight : toPixelValue(height);
    const currentFingerprint = createLayoutFingerprint(data.length, expandedNodes, effectiveWidth, effectiveHeight);
    if (debug) {
      console.log('📊 Change detection:', {
        dataChanges: {
          added: dataChanges.added.length,
          removed: dataChanges.removed.length,
          modified: dataChanges.modified.length,
          unchanged: dataChanges.unchanged.length
        },
        expandChange: {
          type: expandChange.type,
          nodeId: expandChange.nodeId
        },
        layoutDecision,
        canUseIncremental,
        expandedNodesCount: expandedNodes.size,
        expandedNodesList: Array.from(expandedNodes)
      });
    }
    // OPTIMIZATION 1: Try incremental layout for expand/collapse (90% faster!) - Skip if focusing
    if (canUseIncremental && layoutCacheRef.current && expandChange.nodeId) {
      if (debug) console.log('⚡ Using incremental layout update');
      const previousLayout = cacheToLayoutResult(layoutCacheRef.current);
      const isExpanding = expandChange.type === 'expand';
      const transformedForIncremental = transformApiData(data);
      const incrementalResult = performIncrementalLayout(
        previousLayout,
        expandChange.nodeId,
        isExpanding,
        transformedForIncremental,
        expandedNodes
      );
      // Update cache with incremental result
      const updatedCache = createLayoutCache(incrementalResult, expandedNodes, currentFingerprint);
      layoutCacheRef.current = updatedCache;
      // Update tracking refs
      previousDataRef.current = [...data];
      previousExpandedRef.current = new Set(expandedNodes);
      if (debug) console.log('✅ Incremental layout completed');
      return { transformedData: transformedForIncremental, layout: incrementalResult };
    }
    // OPTIMIZATION 2: Try cached layout if possible
    if (canUseCachedLayout(layoutCacheRef.current, currentFingerprint) && !layoutDecision.fullRecalc) {
      if (debug) console.log('✅ Using cached layout');
      const result = {
        transformedData: transformApiData(data),
        layout: cacheToLayoutResult(layoutCacheRef.current!)
      };
      return result;
    }
    // FALLBACK: Full recalculation needed - cache miss
    if (debug) console.log('🔄 Full layout recalculation');
    const transformed = transformApiData(data, debug);
    const baseLayoutResult = filterMode ? 
      createTiledLayout(transformed, effectiveWidth, effectiveHeight, expandedNodes, data, sortBy || SortBy.Alphabetical, filterMode, debug) : 
      createDynamicLayout(transformed, effectiveWidth, effectiveHeight, expandedNodes, data, sortBy || SortBy.Alphabetical, debug);
    const layoutResult = baseLayoutResult;
    // Debug logging for layout results
    if (debug) {
      console.log('📐 Layout calculation results:', filterMode ? 'Tiled' : 'Hierarchical', {
        layoutMode: filterMode ? 'Tiled (centered grid)' : 'Hierarchical (tree structure)',
        totalNodes: layoutResult.nodes.length,
        totalLinks: layoutResult.links.length,
        totalLegLabels: layoutResult.legLabels.length,
        expandedNodes: Array.from(expandedNodes),
        nodesByLevel: layoutResult.nodes.reduce((acc, node) => {
          acc[node.level] = (acc[node.level] || 0) + 1;
          return acc;
        }, {} as Record<number, number>)
      });
      if (!filterMode) {
        // Debug leg labels specifically for hierarchical mode
      console.log('🏷️ Leg labels generated:', layoutResult.legLabels.map(label => ({
        legNumber: label.legNumber,
        position: { x: label.x, y: label.y },
        memberCount: label.memberCount,
        partnerCount: label.partnerCount
      })));
        // Show which nodes are at each level for hierarchical mode
      const nodesByLevel = layoutResult.nodes.reduce((acc, node) => {
        if (!acc[node.level]) acc[node.level] = [];
        acc[node.level].push(`${node.firstName} ${node.lastName} (${node.id})`);
        return acc;
      }, {} as Record<number, string[]>);
      Object.keys(nodesByLevel).forEach(level => {
        console.log(`  Level ${level}: ${nodesByLevel[parseInt(level)].length} nodes`);
      });
      } else {
        // Debug tiled layout grid info
        const totalNodes = layoutResult.nodes.length;
        const aspectRatio = layoutWidth / toPixelValue(height);
        const cols = Math.ceil(Math.sqrt(totalNodes * aspectRatio));
        const rows = Math.ceil(totalNodes / cols);
        console.log('🎯 Tiled layout grid info:', {
          gridDimensions: { cols, rows },
          totalCells: cols * rows,
          utilization: `${totalNodes}/${cols * rows} (${Math.round((totalNodes / (cols * rows)) * 100)}%)`
        });
      }
    }
    // Update cache
    layoutCacheRef.current = createLayoutCache(layoutResult, expandedNodes, currentFingerprint);
    // Update tracking refs
    previousDataRef.current = [...data];
    previousExpandedRef.current = new Set(expandedNodes);
    if (debug) console.log('✅ Layout calculation completed');
    return { 
      transformedData: transformed, 
      layout: layoutResult
    };
  }, [data, layoutWidth, height, expandedNodes, debug, filterMode, sortBy, isFullscreen]);
  // Update debug metrics outside of render cycle for better performance
  useEffect(() => {
    if (debug) {
      updateDebugMetrics({ 
        nodeCount: layout.nodes.length,
        lastOperation: expandedNodes.size > 0 ? `${filterMode ? 'Tiled' : 'Hierarchical'} Layout Update` : `Initial ${filterMode ? 'Tiled' : 'Hierarchical'} Layout`
      });
      // Debug leg labels (only for hierarchical mode)
      if (!filterMode && layout.legLabels.length > 0) {
        console.log('🏷️ Leg labels in layout:', layout.legLabels);
      } else if (!filterMode) {
        console.log('⚠️ No leg labels found in layout!');
      }
    }
  }, [layout.nodes.length, layout.legLabels, debug, updateDebugMetrics, expandedNodes.size, filterMode]);
  // OPTIMIZATION 3: Virtual scrolling for massive rendering performance gains
  const virtualizedResult = useVirtualizedNodes(layout.nodes, viewport, virtualization);
  const { visibleNodes, visibleNodeIds, totalVisible, totalHidden } = virtualizedResult;
  // Helper function to find the path from root to a specific node (removed - unused)
  // Removed unused expand-path helper
  // Helper function to find a node's direct parent in the data structure
  const findDirectParent = useCallback((nodeId: string, transformedData: NodeData[]): NodeData | null => {
    const findParentRecursive = (nodes: NodeData[]): NodeData | null => {
      for (const node of nodes) {
        if (node.children) {
          // Check if this node is the direct parent of our target
          const isDirectParent = node.children.some(child => child.id === nodeId);
          if (isDirectParent) {
            return node;
          }
          // Recursively search in children
          const foundParent = findParentRecursive(node.children);
          if (foundParent) {
            return foundParent;
          }
        }
      }
      return null;
    };
    return findParentRecursive(transformedData);
  }, []);
  // Simple iterative function to expand all parents from selected node to root
  const expandParentsToRoot = useCallback((nodeId: string, transformedData: NodeData[]) => {
    if (debug) {
      console.log('🌱 Starting simple parent expansion for node:', nodeId);
    }
    let currentNodeId = nodeId;
    const parentsToExpand: string[] = [];
    const parentChain: { id: string; name: string }[] = [];
    // First, find all parents from target to root
    while (currentNodeId) {
      const parent = findDirectParent(currentNodeId, transformedData);
      if (parent) {
        parentsToExpand.push(parent.id);
        parentChain.push({ id: parent.id, name: `${parent.firstName} ${parent.lastName}` });
        currentNodeId = parent.id; // Move up to the next parent
        if (debug) {
          console.log(`🔗 Found parent: ${parent.firstName} ${parent.lastName} (${parent.id})`);
        }
      } else {
        // No more parents, we've reached the root
        break;
      }
    }
    if (debug) {
      console.log('🏗️ Complete parent chain found:', {
        targetNode: nodeId,
        parentChainLength: parentChain.length,
        parentsToExpand: parentChain,
        expandingFromBottomToTop: true
      });
    }
    // Now expand all the parents we found
    if (parentsToExpand.length > 0) {
      setExpandedNodes(prev => {
        const newExpanded = new Set(prev);
        const originalSize = prev.size;
        let newlyExpandedCount = 0;
        parentsToExpand.forEach((parentId, index) => {
          const wasAlreadyExpanded = newExpanded.has(parentId);
          newExpanded.add(parentId);
          if (!wasAlreadyExpanded) {
            newlyExpandedCount++;
          }
          if (debug) {
            const parentInfo = parentChain[index];
            console.log('🌿 Expanding parent', `${index + 1}/${parentsToExpand.length}:`, parentInfo.name, `(${parentId})`, {
              wasAlreadyExpanded,
              newlyExpanded: !wasAlreadyExpanded,
              distanceFromTarget: index + 1
            });
          }
        });
        if (debug) {
          console.log('✅ Simple parent expansion completed:', {
            targetNode: nodeId,
            totalParentsFound: parentsToExpand.length,
            expandedCountBefore: originalSize,
            expandedCountAfter: newExpanded.size,
            newlyExpanded: newlyExpandedCount,
            allParentsNowExpanded: parentsToExpand.every(id => newExpanded.has(id))
          });
        }
        return newExpanded;
      });
    } else {
      if (debug) {
        console.log('ℹ️ No parents found to expand for node:', nodeId);
      }
    }
    return parentsToExpand;
  }, [findDirectParent, debug]);
// Function to expand all nodes that have children
const expandAllNodes = useCallback(() => {
  if (debug) {
    console.log('🌳 Starting expand all functionality');
  }
  // Collect all node IDs that have children from the transformed data
  const collectNodesWithChildren = (nodes: NodeData[]): string[] => {
    const nodesWithChildren: string[] = [];
    const traverse = (nodeList: NodeData[]) => {
      nodeList.forEach(node => {
        if (node.children && node.children.length > 0) {
          nodesWithChildren.push(node.id);
          // Recursively check children for more nodes with children
          traverse(node.children);
        }
      });
    };
    traverse(nodes);
    return nodesWithChildren;
  };
  const freshTransformedData = transformApiData(data);
  const allNodesWithChildren = collectNodesWithChildren(freshTransformedData);
  if (debug) {
    console.log('🌳 Found nodes with children to expand:', {
      totalNodesWithChildren: allNodesWithChildren.length,
      nodeIds: allNodesWithChildren
    });
  }
  // Expand all nodes that have children
  setExpandedNodes(prev => {
    const newExpanded = new Set(prev);
    const originalSize = prev.size;
    let newlyExpandedCount = 0;
    allNodesWithChildren.forEach(nodeId => {
      const wasAlreadyExpanded = newExpanded.has(nodeId);
      newExpanded.add(nodeId);
      if (!wasAlreadyExpanded) {
        newlyExpandedCount++;
      }
    });
    if (debug) {
      console.log('✅ Expand all completed:', {
        totalNodesWithChildren: allNodesWithChildren.length,
        expandedCountBefore: originalSize,
        expandedCountAfter: newExpanded.size,
        newlyExpanded: newlyExpandedCount,
        allNodesExpanded: allNodesWithChildren.every(id => newExpanded.has(id))
      });
    }
    return newExpanded;
  });
}, [data, debug]);
// Track previous expandAll value to detect changes
const previousExpandAllRef = useRef(false);
// Handle expandAll trigger
useEffect(() => {
  const wasExpandAll = previousExpandAllRef.current;
  const isExpandAll = expandAll || false;
  // Only expand when expandAll changes from false to true
  if (!wasExpandAll && isExpandAll) {
    if (debug) {
      console.log('🎯 Expand all triggered from parent component');
    }
    expandAllNodes();
    // Notify parent when expansion is complete with a small delay
    setTimeout(() => {
      if (onExpandAllComplete) {
        const currentExpandedCount = expandedNodes.size;
        onExpandAllComplete(currentExpandedCount);
        if (debug) {
          console.log('📢 Notified parent: expand all complete with', currentExpandedCount, 'nodes');
        }
      }
    }, 100);
  }
  previousExpandAllRef.current = isExpandAll;
}, [expandAll, onExpandAllComplete, expandAllNodes, debug, expandedNodes.size]);
  // Removed unused expanded-nodes debug helper
  // Test function for debugging parent expansion (can be called from browser console)
  const testParentExpansion = useCallback((nodeId: string) => {
    if (debug) {
      console.log('🧪 Testing simple parent expansion for node:', nodeId);
      const freshData = transformApiData(data);
      // Test the parent finding chain
      let currentId = nodeId;
      const parentChain = [];
      console.log('🧪 Tracing parent chain:');
      while (currentId) {
        const parent = findDirectParent(currentId, freshData);
        if (parent) {
          parentChain.push({
            childId: currentId,
            parentId: parent.id,
            parentName: `${parent.firstName} ${parent.lastName}`
          });
          console.log(`  🔗 ${currentId} -> ${parent.id} (${parent.firstName} ${parent.lastName})`);
          currentId = parent.id;
        } else {
          console.log(`  🏁 Reached root for: ${currentId}`);
          break;
        }
      }
      console.log('🧪 Test results:', {
        nodeId,
        parentChainLength: parentChain.length,
        parentChain,
        dataStructure: {
          totalApiItems: data.length,
          transformedRoots: freshData.length,
          rootNodes: freshData.map(root => ({
            id: root.id,
            name: `${root.firstName} ${root.lastName}`,
            childrenCount: root.children?.length || 0
          }))
        }
      });
      // Actually test the expansion
      const expandedParents = expandParentsToRoot(nodeId, freshData);
      console.log('🧪 Expansion test completed, parents expanded:', expandedParents);
      return { parentChain, expandedParents };
    }
    return { parentChain: [], expandedParents: [] };
  }, [debug, data, findDirectParent, expandParentsToRoot]);
  // Enhanced scroll to node function that centers the node in viewport
  const scrollToNodeCentered = useCallback((nodeId: string, retryCount: number = 0, forceScroll: boolean = false) => {
    // Allow scrolling when:
    // 1. Tree has focus (user-initiated)
    // 2. Force scroll is enabled (programmatic, like filter mode transitions)
    // 3. Not in filter mode (tree mode operations)
    if (!forceScroll && !isTreeFocused && filterMode) {
      if (debug) {
        console.log('🚫 Scrolling disabled - tree not focused and in filter mode');
      }
      return;
    }
    if (!scrollableAreaRef.current) {
      if (debug) {
        console.log('⚠️ Scrollable area ref not available for scrolling');
      }
      return;
    }
    // Find the node in the current layout
    const node = layout.nodes.find(n => n.id === nodeId);
    if (!node) {
      if (debug) {
        console.log('⚠️ Node not found in layout for scrolling:', {
          nodeId,
          layoutNodesCount: layout.nodes.length,
          availableNodeIds: layout.nodes.slice(0, 5).map(n => n.id),
          retryCount
        });
      }
      // Retry up to 3 times with increasing delays
      if (retryCount < 3) {
        setTimeout(() => {
          if (debug) {
            console.log('🔄 Retrying scroll to node:', nodeId, `(attempt ${retryCount + 1}/3)`);
          }
          scrollToNodeCentered(nodeId, retryCount + 1, forceScroll);
        }, 200 * (retryCount + 1)); // 200ms, 400ms, 600ms delays
      }
      return;
    }
    const container = scrollableAreaRef.current;
    // Node dimensions
    const NODE_WIDTH = 180;
    const NODE_HEIGHT = 32;
    // Calculate node's absolute position
    const nodeLeft = node.x;
    const nodeTop = node.y;
    const nodeCenterX = nodeLeft + NODE_WIDTH / 2;
    const nodeCenterY = nodeTop + NODE_HEIGHT / 2;
    // Container dimensions
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    // Calculate scroll position to center the node
    const targetScrollLeft = Math.max(0, nodeCenterX - containerWidth / 2);
    const targetScrollTop = Math.max(0, nodeCenterY - containerHeight / 2);
    if (debug) {
      console.log('📺 Centering node in viewport:', {
        nodeId,
        retryCount,
        nodePosition: { x: nodeLeft, y: nodeTop },
        nodeCenter: { x: nodeCenterX, y: nodeCenterY },
        containerSize: { width: containerWidth, height: containerHeight },
        scrollTo: { left: targetScrollLeft, top: targetScrollTop },
        currentScroll: { left: container.scrollLeft, top: container.scrollTop }
      });
    }
    // Smooth scroll to center the node
      container.scrollTo({
      left: targetScrollLeft,
      top: targetScrollTop,
        behavior: 'smooth'
      });
    // Verify scroll happened after a short delay
      if (debug) {
      setTimeout(() => {
        console.log('📺 Scroll verification:', {
          nodeId,
          finalScroll: { left: container.scrollLeft, top: container.scrollTop },
          targetScroll: { left: targetScrollLeft, top: targetScrollTop },
          scrollDifference: {
            x: Math.abs(container.scrollLeft - targetScrollLeft),
            y: Math.abs(container.scrollTop - targetScrollTop)
          }
        });
      }, 1000);
    }
  }, [isTreeFocused, filterMode, layout.nodes, debug]);
  // Function that waits for layout to include target node before scrolling
  const waitForLayoutAndScroll = useCallback((nodeId: string, maxWaitTime: number = 3000) => {
    const startTime = Date.now();
    const checkAndScroll = () => {
      const currentTime = Date.now();
      const elapsedTime = currentTime - startTime;
      // Find the node in the current layout
      const node = layout.nodes.find(n => n.id === nodeId);
      if (node) {
        if (debug) {
          console.log('🎯 Layout ready, node found, scrolling:', {
            nodeId,
            nodePosition: { x: node.x, y: node.y },
            elapsedTime: `${elapsedTime}ms`,
            layoutNodesCount: layout.nodes.length
          });
        }
        scrollToNodeCentered(nodeId);
        return;
      }
      // If node not found and we haven't exceeded max wait time, retry
      if (elapsedTime < maxWaitTime) {
        if (debug) {
          console.log(`⏳ Waiting for layout update (${elapsedTime}ms/${maxWaitTime}ms)...`);
        }
        setTimeout(checkAndScroll, 100); // Check every 100ms
      } else {
        if (debug) {
          console.log('⚠️ Timeout waiting for layout, forcing scroll anyway:', {
            nodeId,
            elapsedTime: `${elapsedTime}ms`,
            layoutNodesCount: layout.nodes.length
          });
        }
        scrollToNodeCentered(nodeId); // Force scroll even if node not found
      }
    };
    checkAndScroll();
  }, [layout.nodes, debug, scrollToNodeCentered]);
  // Expose debug functions globally (after function declarations to avoid hoisting issues)
  useEffect(() => {
    if (debug && typeof window !== 'undefined') {
      const debugWindow = window as Window & {
        testGenealogyParentExpansion?: typeof testParentExpansion;
        testGenealogyScrollToNode?: (nodeId: string) => void;
        testGenealogyScrollToNodeImmediate?: (nodeId: string) => void;
        testGenealogyFocusNode?: (nodeId: string) => void;
      };
      debugWindow.testGenealogyParentExpansion = testParentExpansion;
      debugWindow.testGenealogyScrollToNode = (nodeId: string) => {
        console.log('🧪 Testing scroll to node:', nodeId);
        waitForLayoutAndScroll(nodeId);
      };
      debugWindow.testGenealogyScrollToNodeImmediate = (nodeId: string) => {
        console.log('🧪 Testing immediate scroll to node:', nodeId);
        scrollToNodeCentered(nodeId);
      };
      debugWindow.testGenealogyFocusNode = (nodeId: string) => {
        console.log('🧪 Testing new simplified focus mechanism:', nodeId);
        const freshTransformedData = transformApiData(data);
        expandParentsToRoot(nodeId, freshTransformedData);
        setNodeToCenter(nodeId);
      };
      console.log('🧪 Debug functions exposed:');
      console.log('  - window.testGenealogyParentExpansion(nodeId)');
      console.log('  - window.testGenealogyScrollToNode(nodeId) // Smart wait');
      console.log('  - window.testGenealogyScrollToNodeImmediate(nodeId) // Immediate');
      console.log('  - window.testGenealogyFocusNode(nodeId) // New simplified focus');
    }
      }, [debug, testParentExpansion, waitForLayoutAndScroll, scrollToNodeCentered, data, expandParentsToRoot]);
  // Filter links to only render those connecting visible nodes
  const visibleLinks = useMemo(() => {
    return getVisibleLinks(layout.links, visibleNodeIds);
  }, [layout.links, visibleNodeIds]);
  // Virtualization performance stats
  const virtualizationStats = useMemo(() => {
    return getVirtualizationStats(layout.nodes.length, totalVisible, viewport);
  }, [layout.nodes.length, totalVisible, viewport]);
  // Update debug metrics with virtualization info
  useEffect(() => {
    if (debug && virtualization) {
      updateDebugMetrics({
        lastOperation: `${filterMode ? 'Tiled' : 'Hierarchical'} Virtualized: ${totalVisible}/${layout.nodes.length} nodes visible`,
      });
      // Log detailed virtualization info
      console.log('🎬 Virtualization Debug Info:', {
        isFullscreen,
        totalNodes: layout.nodes.length,
        visibleNodes: totalVisible,
        hiddenNodes: totalHidden,
        viewport,
        virtualizationEnabled: virtualization,
        firstVisibleNode: visibleNodes[0] ? `${visibleNodes[0].firstName} ${visibleNodes[0].lastName} at (${visibleNodes[0].x}, ${visibleNodes[0].y})` : 'None',
        lastVisibleNode: visibleNodes[visibleNodes.length - 1] ? `${visibleNodes[visibleNodes.length - 1].firstName} ${visibleNodes[visibleNodes.length - 1].lastName} at (${visibleNodes[visibleNodes.length - 1].x}, ${visibleNodes[visibleNodes.length - 1].y})` : 'None'
      });
    }
  }, [debug, virtualization, totalVisible, totalHidden, layout.nodes.length, updateDebugMetrics, filterMode, isFullscreen, viewport, visibleNodes]);
  // (handleNodeClick will be defined after focusOnNode)
  const handleNodeExpand = useCallback((nodeData: NodeData) => {
    const isExpanding = !expandedNodes.has(nodeData.id);
    if (debug) {
      console.log('🔄 Toggling node:', isExpanding ? 'Expanding' : 'Collapsing', nodeData.id, {
        nodeHasChildren: nodeData.children?.length || 0,
        currentlyExpanded: expandedNodes.has(nodeData.id)
      });
    }
    // Select the node when expanding/collapsing (only if not externally controlled)
    if (externalSelectedNodeId === undefined) {
      setInternalSelectedNodeId(nodeData.id);
    }
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodeData.id)) {
        newSet.delete(nodeData.id);
        if (debug) console.log('📦 Collapsed node:', nodeData.id);
      } else {
        newSet.add(nodeData.id);
        if (debug) console.log('📂 Expanded node:', nodeData.id, 'Children count:', nodeData.children?.length || 0);
      }
      return newSet;
    });
    onNodeExpand?.(nodeData);
  }, [expandedNodes, debug, onNodeExpand, externalSelectedNodeId]);
  // Handle node click with proper focus functionality - MOVED BEFORE handleKeyDown to fix dependency order
  const handleNodeClick = useCallback((nodeData: NodeData) => {
    if (debug) {
      console.log('🖱️ Node clicked:', nodeData.id, `filterMode: ${filterMode}`);
    }
    // Prevent root node selection
    if (nodeData.treeDepth === 0) {
      if (debug) {
        console.log('🚫 Root node click ignored - root nodes are not selectable');
      }
      return;
    }
    // Call the external onNodeClick callback for non-root nodes
    onNodeClick?.(nodeData);
    if (filterMode) {
      // Store the node info for debugging
      if (debug) {
        console.log('🔄 Switching from filter to tree mode and focusing on node:', {
          nodeId: nodeData.id,
          nodeName: `${nodeData.firstName} ${nodeData.lastName}`,
          action: 'Setting selected, switching mode, expanding parents, centering'
        });
      }
      // Set the selected node
      setInternalSelectedNodeId(nodeData.id);
      // Switch to tree mode
      onFilterModeChange?.(false);
      // Delay the parent expansion and centering to allow mode switch to complete
      setTimeout(() => {
        // Expand all parent nodes to make the selected node visible
        const freshTransformedData = transformApiData(data);
        expandParentsToRoot(nodeData.id, freshTransformedData);
        // Center the node in viewport
        setNodeToCenter(nodeData.id);
        // Ensure focus is restored after all operations complete
        setTimeout(() => {
          if (treeContainerRef.current && 
              !document.activeElement?.matches('input, textarea')) {
            treeContainerRef.current.focus();
            if (debug) {
              console.log('🎯 Focus restored after node click and mode switch');
            }
          }
        }, 300);
      }, 100);
    } else {
      // In tree mode, just select the node
      setInternalSelectedNodeId(nodeData.id);
    }
  }, [filterMode, onFilterModeChange, expandParentsToRoot, debug, data, onNodeClick]);
  // Keyboard navigation handler
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    // Only handle keyboard events when the tree has focus
    if (!isTreeFocused) {
      if (debug) {
        console.log('🚫 Keyboard handling disabled - tree not focused');
      }
      return;
    }
    const isArrowKey = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key);
    const isEnterKey = event.key === 'Enter';
    if (!isArrowKey && !isEnterKey) {
      return;
    }
    // Handle Enter key - act like clicking the selected node
    if (isEnterKey) {
    event.preventDefault();
      event.stopPropagation();
      if (selectedNodeId) {
        // Find the selected node data using the local findNodeById function
        const findNodeById = (nodeId: string, nodes: NodeData[]): NodeData | null => {
          for (const node of nodes) {
            if (node.id === nodeId) return node;
            if (node.children) {
              const found = findNodeById(nodeId, node.children);
              if (found) return found;
            }
          }
          return null;
        };
        const foundNode = findNodeById(selectedNodeId, transformedData);
        if (foundNode) {
          if (debug) {
            console.log(`⌨️ Enter key pressed on node: ${foundNode.firstName} ${foundNode.lastName} (${foundNode.id})`);
          }
          // Trigger the same behavior as clicking the node
          handleNodeClick(foundNode);
        } else {
          if (debug) {
            console.log(`⌨️ Enter key pressed but could not find node: ${selectedNodeId}`);
          }
        }
      } else {
        if (debug) {
          console.log('⌨️ Enter key pressed but no node selected');
        }
      }
      return;
    }
    // CRITICAL: Prevent any default browser behavior for arrow keys
    event.preventDefault();
    event.stopPropagation();
    // Ensure the tree container has focus for navigation
    if (treeContainerRef.current && document.activeElement !== treeContainerRef.current) {
      treeContainerRef.current.focus();
      if (debug) {
        console.log('🎯 Focused tree container for arrow key navigation');
      }
    }
    if (debug) {
      console.log(`🎯 Arrow key pressed: ${event.key} - current selected: ${selectedNodeId || 'none'}`);
    }
    // If nothing is selected, start from first available node
    if (!selectedNodeId) {
      const firstNode = filterMode ? 
        (layout.nodes.length > 0 ? layout.nodes[0] : null) : // First node in tiled layout
        navigationUtils.findFirstChildOfRoot(transformedData, sortBy || SortBy.Alphabetical); // First child of root in hierarchical
      if (firstNode && externalSelectedNodeId === undefined) {
        setInternalSelectedNodeId(firstNode.id);
        // Scroll to the initial selected node
        setTimeout(() => scrollToNodeCentered(firstNode.id), 100);
        if (debug) console.log('🎯 Keyboard nav: Selected first node', firstNode.id);
      }
      return;
    }
    if (debug) {
      console.log('🎯 handleKeyDown start:', {
        key: event.key,
        selectedNodeId,
        isTreeFocused,
        filterMode,
        transformedDataLength: transformedData.length,
        sortBy: sortBy || 'undefined',
        sortByType: typeof sortBy,
        sortByEnum: sortBy,
        navigationUtilsExists: !!navigationUtils,
        layoutNodesCount: layout.nodes.length
      });
    }
    let targetNode: NodeData | null = null;
    if (filterMode) {
      // GRID-BASED NAVIGATION for filter mode
      const currentNodeIndex = layout.nodes.findIndex(node => node.id === selectedNodeId);
      if (currentNodeIndex === -1) return;
      // Calculate grid dimensions (same logic as in createTiledLayout)
      const totalNodes = layout.nodes.length;
      const aspectRatio = layoutWidth / toPixelValue(height);
      const cols = Math.ceil(Math.sqrt(totalNodes * aspectRatio));
      const rows = Math.ceil(totalNodes / cols);
      // Calculate current position in grid
      const currentCol = currentNodeIndex % cols;
      const currentRow = Math.floor(currentNodeIndex / cols);
      let targetCol = currentCol;
      let targetRow = currentRow;
      // Calculate target position based on arrow key
      switch (event.key) {
        case 'ArrowLeft':
          targetCol = Math.max(0, currentCol - 1);
          break;
        case 'ArrowRight':
          targetCol = Math.min(cols - 1, currentCol + 1);
          break;
        case 'ArrowUp':
          targetRow = Math.max(0, currentRow - 1);
          break;
        case 'ArrowDown':
          targetRow = Math.min(rows - 1, currentRow + 1);
          break;
      }
      // Calculate target index and find target node
      const targetIndex = targetRow * cols + targetCol;
      if (targetIndex >= 0 && targetIndex < layout.nodes.length) {
        targetNode = layout.nodes[targetIndex];
      }
      if (debug) {
        console.log('🎯 Grid navigation:', {
          key: event.key,
          gridDimensions: { cols, rows },
          currentPosition: { col: currentCol, row: currentRow, index: currentNodeIndex },
          targetPosition: { col: targetCol, row: targetRow, index: targetIndex },
          targetFound: !!targetNode
        });
      }
    } else {
      // HIERARCHICAL NAVIGATION for tree mode (existing logic)
    switch (event.key) {
      case 'ArrowLeft':
        // Check if current node is a direct child of root first
        if (transformedData.length > 0) {
          const rootNode = transformedData[0];
          const isDirectChildOfRoot = rootNode.children?.some(child => child.id === selectedNodeId);
          if (isDirectChildOfRoot) {
            // Prevent navigation from root's children back to root
            if (debug) console.log('🚫 Prevented left arrow navigation from root child to root');
            return; // Exit early, don't set targetNode
          }
        }
        // Only allow navigation for nodes that are NOT direct children of root
        targetNode = navigationUtils.findParent(selectedNodeId, transformedData);
        break;
      case 'ArrowRight': {
        // Select and expand the first child (go forward)
        // First, find the current node
        let currentNode: NodeData | null = null;
        for (const rootNode of transformedData) {
          currentNode = findNodeInTree(rootNode, selectedNodeId);
          if (currentNode) break;
        }
        if (currentNode && currentNode.children && currentNode.children.length > 0) {
          // If node has children but is not expanded, expand it first
          if (!expandedNodes.has(selectedNodeId)) {
            handleNodeExpand(currentNode);
            if (debug) console.log('🔄 Keyboard nav: Expanded node for right navigation', selectedNodeId);
          }
          // Always try to select the first child (using appropriate sorting)
          // Use centralized sorting logic for consistency
          const isRootNode = currentNode.treeDepth === 0;
          const sortedChildren = applyCentralizedSorting(
            currentNode.children,
            isRootNode,
            sortBy || SortBy.Alphabetical,
            data,
            debug
          );
          targetNode = sortedChildren[0];
        }
        break;
      }
      case 'ArrowDown':
        // Select next sibling
        if (debug) {
          const currentNode = transformedData.find(root => {
            const findNode = (node: NodeData, nodeId: string): NodeData | null => {
              if (node.id === nodeId) return node;
              if (node.children) {
                for (const child of node.children) {
                  const found = findNode(child, nodeId);
                  if (found) return found;
                }
              }
              return null;
            };
            return findNode(root, selectedNodeId);
          });
          console.log('🔽 ArrowDown pressed:', {
            nodeId: selectedNodeId,
            currentNode: currentNode ? `${currentNode.firstName} ${currentNode.lastName}` : 'not found',
            treeDepth: currentNode?.treeDepth || 'unknown'
          });
        }
        targetNode = navigationUtils.findNextSibling(selectedNodeId, transformedData, sortBy || SortBy.Alphabetical);
        if (debug) {
          console.log('🔽 ArrowDown result:', targetNode ? `${targetNode.firstName} ${targetNode.lastName}` : 'No target found');
        }
        break;
      case 'ArrowUp':
        // Select previous sibling
        if (debug) {
          const currentNode = transformedData.find(root => {
            const findNode = (node: NodeData, nodeId: string): NodeData | null => {
              if (node.id === nodeId) return node;
              if (node.children) {
                for (const child of node.children) {
                  const found = findNode(child, nodeId);
                  if (found) return found;
                }
              }
              return null;
            };
            return findNode(root, selectedNodeId);
          });
          console.log('🔼 ArrowUp pressed:', {
            nodeId: selectedNodeId,
            currentNode: currentNode ? `${currentNode.firstName} ${currentNode.lastName}` : 'not found',
            treeDepth: currentNode?.treeDepth || 'unknown'
          });
        }
        targetNode = navigationUtils.findPreviousSibling(selectedNodeId, transformedData, sortBy || SortBy.Alphabetical);
        if (debug) {
          console.log('🔼 ArrowUp result:', targetNode ? `${targetNode.firstName} ${targetNode.lastName}` : 'No target found');
        }
        break;
      }
    }
    if (debug) {
      console.log('🎯 handleKeyDown result:', {
        targetNodeFound: !!targetNode,
        targetNodeId: targetNode?.id || 'none',
        targetNodeName: targetNode ? `${targetNode.firstName} ${targetNode.lastName}` : 'none',
        targetTreeDepth: targetNode?.treeDepth || 'none',
        externalSelectedNodeId: externalSelectedNodeId || 'undefined',
        willUpdate: !!(targetNode && externalSelectedNodeId === undefined)
      });
    }
    if (targetNode && externalSelectedNodeId === undefined) {
      setInternalSelectedNodeId(targetNode.id);
      // Scroll to the newly selected node
      setTimeout(() => {
        if (targetNode) {
          scrollToNodeCentered(targetNode.id);
        }
      }, 100); // Small delay to allow state update
      if (debug) {
        console.log('🎯 Keyboard nav:', event.key, '-> Selected node', targetNode.id);
      }
    }
  }, [
    isTreeFocused,
    debug, 
    filterMode, 
    transformedData, 
    selectedNodeId, 
    handleNodeClick, 
    navigationUtils, 
    setInternalSelectedNodeId, 
    handleNodeExpand,
    layout.nodes,
    height,
    expandedNodes,
    externalSelectedNodeId,
    scrollToNodeCentered,
    sortBy,
    data,
    layoutWidth
  ]);
  // Calculate canvas dimensions and animation state (always calculate, even for empty data)
  const baseCanvasWidth = Math.max(layoutWidth, layout.nodes.length > 0 ? Math.max(...layout.nodes.map((n: LayoutNode) => n.x)) + 300 : 1000);
  const canvasHeight = Math.max(toPixelValue(height), layout.nodes.length > 0 ? Math.max(...layout.nodes.map((n: LayoutNode) => n.y)) + 100 : 500);
  // Use mobile pages total width if in mobile mode, otherwise use base canvas width
    const canvasWidth = baseCanvasWidth;
  // No zoom animation - always normal scale
  const getCanvasTransform = () => {
        return 'scale(1)';
  };
  // Debug logging (after function declarations to avoid hoisting issues)
  if (debug) {
    console.log('🔍 Debug info:', {
      dataLength: data.length,
      layoutNodesLength: layout.nodes.length,
      visibleNodesLength: totalVisible,
      hiddenNodesLength: totalHidden,
      renderingReduction: virtualizationStats.renderingReduction + '%',
      expandedNodesSize: expandedNodes.size,
      rootCustomer: rootCustomer?.customer.unicity,
      virtualizationEnabled: virtualization,
      layoutMode: filterMode ? 'Tiled (centered grid)' : 'Hierarchical (tree structure)',
      filterModeEnabled: filterMode
    });
  }
  // Check if layout produced any nodes (for debugging)
  if (layout.nodes.length === 0 && debug) {
    console.log('⚠️ No nodes generated from layout');
  }
  // (renderedNodes will be defined after handleNodeClick)
  // Simple effect to handle centering when nodeToCenter is set
  useEffect(() => {
    if (!nodeToCenter || filterMode) return; // Only center when not in filter mode
    if (debug) {
      console.log('🎯 Attempting to center node:', nodeToCenter);
    }
    // Clear any existing timeout
    if (centeringTimeoutRef.current) {
      clearTimeout(centeringTimeoutRef.current);
    }
    // Try to find and center the node with retries
    let attempts = 0;
    const maxAttempts = 20; // Try for up to 2 seconds
    const attemptCenter = () => {
      const nodeInLayout = layout.nodes.find(n => n.id === nodeToCenter);
      attempts++;
      if (nodeInLayout) {
        if (debug) {
          console.log('✅ Node found in layout, centering:', {
            nodeId: nodeToCenter,
            position: { x: nodeInLayout.x, y: nodeInLayout.y },
            attempts,
            layoutNodesCount: layout.nodes.length
          });
        }
        // Center the node (force scroll since this is programmatic)
        scrollToNodeCentered(nodeToCenter, 0, true);
        // Clear the centering state
        setNodeToCenter(null);
      } else if (attempts < maxAttempts) {
        if (debug) {
          console.log(`⏳ Node not found, attempt ${attempts}/${maxAttempts}`);
        }
        // Try again in 100ms
        centeringTimeoutRef.current = setTimeout(attemptCenter, 100);
      } else {
        if (debug) {
          console.log('❌ Failed to find node after max attempts:', nodeToCenter);
        }
        // Give up and clear the state
        setNodeToCenter(null);
      }
    };
    // Start attempting to center after a small delay
    centeringTimeoutRef.current = setTimeout(attemptCenter, 100);
    // Cleanup function
    return () => {
      if (centeringTimeoutRef.current) {
        clearTimeout(centeringTimeoutRef.current);
      }
    };
  }, [nodeToCenter, layout.nodes, filterMode, debug, scrollToNodeCentered]);
  // Backward compatibility function for existing code
  // const scrollToNode = useCallback((nodeId: string) => {
  //   scrollToNodeCentered(nodeId);
  // }, [scrollToNodeCentered]);
  // Optimize nodes rendering with virtualization - only render visible nodes
  const renderedNodes = useMemo(() => {
    const nodesToRender = virtualization ? visibleNodes : layout.nodes;
    if (debug) {
      console.log(`🎨 Rendering ${nodesToRender.length}/${layout.nodes.length} nodes (${virtualizationStats.renderingReduction}% reduction)`);
    }
    return nodesToRender.map((node: LayoutNode) => (
      <Node
        key={node.id}
        data={node}
        x={node.x}
        y={node.y}
        onClick={handleNodeClick}
        onExpand={filterMode ? undefined : handleNodeExpand} // Disable expand/collapse in filter mode
        isRoot={node.level === 0}
        isExpanded={expandedNodes.has(node.id)}
        isSelected={selectedNodeId === node.id}
        showExpandButton={!filterMode} // Hide expand button in filter mode
      />
    ));
  }, [
    virtualization, 
    visibleNodes, 
    layout.nodes, 
    expandedNodes, 
    selectedNodeId, 
    handleNodeClick, 
    handleNodeExpand, 
    debug,
    virtualizationStats.renderingReduction,
    filterMode // Add filterMode dependency (controls both onExpand and showExpandButton)
  ]);
  // Cleanup centering timeout on unmount
  useEffect(() => {
    return () => {
      if (centeringTimeoutRef.current) {
        clearTimeout(centeringTimeoutRef.current);
      }
    };
  }, []);
  // Auto-focus the tree container for keyboard navigation (only if autoFocus prop is true)
  useEffect(() => {
    if (autoFocus && treeContainerRef.current && !filterMode) {
      // Only auto-focus in tree mode, not filter mode
      setTimeout(() => {
        treeContainerRef.current?.focus();
        if (debug) {
          console.log('🎯 Auto-focused tree container for keyboard navigation');
        }
      }, 200);
    }
  }, [autoFocus, debug, filterMode]);
  // Debug logging for sortBy changes
  useEffect(() => {
        if (debug) {
      console.log('🔄 sortBy changed to:', sortBy, '- Navigation utilities will be recreated');
    }
  }, [sortBy, debug]);
  // Set default selection to Leg 1 (first child of root) when component loads
  useEffect(() => {
    // Only set default if no external selection is provided and no internal selection exists
    if (externalSelectedNodeId === undefined && !internalSelectedNodeId && transformedData.length > 0) {
      const firstChildOfRoot = navigationUtils.findFirstChildOfRoot(transformedData, sortBy || SortBy.Alphabetical);
      if (firstChildOfRoot) {
        setInternalSelectedNodeId(firstChildOfRoot.id);
        if (debug) {
          console.log('🎯 Set default selection to Leg 1:', firstChildOfRoot.firstName, firstChildOfRoot.lastName);
        }
      }
    }
  }, [transformedData, externalSelectedNodeId, internalSelectedNodeId, navigationUtils, debug, sortBy]);
  // Note: Removed aggressive focus management - now using localized focus system
  // Note: Focus is now managed locally through click and focus/blur events
  // Removed animation phase focus restoration since there's no animation
  // Function to collapse all nodes
  const collapseAllNodes = useCallback(() => {
    if (debug) {
      console.log('🌳 Starting collapse all functionality');
    }
    setExpandedNodes(prev => {
      const originalSize = prev.size;
      if (debug) {
        console.log('✅ Collapse all completed:', {
          expandedCountBefore: originalSize,
          expandedCountAfter: 0,
          allNodesCollapsed: true
        });
      }
      return new Set(); // Clear all expanded nodes
    });
  }, [debug]);
  // Internal expand/collapse all toggle
  const handleExpandCollapseAll = useCallback(() => {
    const hasAnyExpanded = expandedNodes.size > 0;
    if (hasAnyExpanded) {
      collapseAllNodes();
    } else {
      expandAllNodes();
    }
  }, [expandedNodes.size, collapseAllNodes, expandAllNodes]);
  // Function to handle fullscreen change events
  const handleFullscreenChange = useCallback(() => {
    const vendorDocument = document as Document & {
      webkitFullscreenElement?: Element | null;
      mozFullScreenElement?: Element | null;
      msFullscreenElement?: Element | null;
    };
    const isCurrentlyFullscreen = !!(
      document.fullscreenElement ||
      vendorDocument.webkitFullscreenElement ||
      vendorDocument.mozFullScreenElement ||
      vendorDocument.msFullscreenElement
    );
    setIsFullscreen(isCurrentlyFullscreen);
          if (debug) {
      console.log('🖥️ Fullscreen state changed:', {
        isFullscreen: isCurrentlyFullscreen,
        fullscreenElement: document.fullscreenElement?.tagName || 'none'
      });
    }
  }, [debug]);
  // Set up fullscreen event listeners
  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [handleFullscreenChange]);
  // Force viewport recalculation when fullscreen state changes
  useEffect(() => {
    // Add a small delay to ensure DOM has updated after fullscreen change
    const timer = setTimeout(() => {
      if (scrollableAreaRef.current) {
        // Trigger a resize event to force viewport recalculation
        const resizeEvent = new Event('resize');
        window.dispatchEvent(resizeEvent);
        // Also directly trigger scroll event to update viewport bounds
        const scrollEvent = new Event('scroll');
        scrollableAreaRef.current.dispatchEvent(scrollEvent);
        if (debug) {
          console.log('🖥️ Forced viewport recalculation for fullscreen change:', {
            isFullscreen,
            scrollableArea: {
              clientWidth: scrollableAreaRef.current.clientWidth,
              clientHeight: scrollableAreaRef.current.clientHeight,
              scrollLeft: scrollableAreaRef.current.scrollLeft,
              scrollTop: scrollableAreaRef.current.scrollTop
            }
          });
        }
      }
    }, 100); // Small delay to allow DOM updates
    return () => clearTimeout(timer);
  }, [isFullscreen, debug]);
  // Function to toggle fullscreen mode
  const handleFullscreenToggle = useCallback(async () => {
    if (!containerRef.current) {
      if (debug) {
        console.log('⚠️ Cannot toggle fullscreen - container ref not available');
      }
      return;
    }
    try {
      if (isFullscreen) {
        // Exit fullscreen
        const vendorDocument = document as Document & {
          webkitExitFullscreen?: () => Promise<void>;
          mozCancelFullScreen?: () => Promise<void>;
          msExitFullscreen?: () => Promise<void>;
        };
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (vendorDocument.webkitExitFullscreen) {
          await vendorDocument.webkitExitFullscreen();
        } else if (vendorDocument.mozCancelFullScreen) {
          await vendorDocument.mozCancelFullScreen();
        } else if (vendorDocument.msExitFullscreen) {
          await vendorDocument.msExitFullscreen();
        }
        if (debug) {
          console.log('🖥️ Exiting fullscreen mode');
        }
      } else {
        // Enter fullscreen
        const element = containerRef.current as HTMLDivElement & {
          webkitRequestFullscreen?: () => Promise<void>;
          mozRequestFullScreen?: () => Promise<void>;
          msRequestFullscreen?: () => Promise<void>;
        };
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          await element.webkitRequestFullscreen();
        } else if (element.mozRequestFullScreen) {
          await element.mozRequestFullScreen();
        } else if (element.msRequestFullscreen) {
          await element.msRequestFullscreen();
        }
            if (debug) {
          console.log('🖥️ Entering fullscreen mode');
        }
      }
    } catch (error) {
              if (debug) {
        console.error('❌ Failed to toggle fullscreen:', error);
      }
    }
  }, [isFullscreen, debug]);
  return (
    <Box 
      ref={containerRef} 
      sx={{ 
        position: 'relative',
        width: isFullscreen ? '100vw' : 'auto',
        height: isFullscreen ? '100vh' : 'auto',
        ...(isFullscreen && {
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 9999,
          backgroundColor: 'background.default',
        })
      }}
    >
      {/* Live region for accessibility announcements */}
      <Box
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: 'absolute',
          left: -10000,
          width: 1,
          height: 1,
          overflow: 'hidden',
        }}
      >
        {selectedNodeId && (() => {
          const selectedNode = layout.nodes.find(n => n.id === selectedNodeId);
          return selectedNode ? `Selected: ${selectedNode.firstName} ${selectedNode.lastName}, Level ${selectedNode.level + 1}` : '';
        })()}
      </Box>
      <TreeContainer 
        width={width}
        height={height}
        isFullscreen={isFullscreen}
        ref={treeContainerRef}
        tabIndex={0}
        role="tree"
        aria-label={`Genealogy tree view with ${layout.nodes.length} members - use arrow keys to navigate, Enter to select`}
        aria-multiselectable="false"
        aria-activedescendant={selectedNodeId ? `tree-node-${selectedNodeId}` : undefined}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          setIsTreeFocused(true);
          if (debug) {
            console.log('🎯 Tree container focused - keyboard navigation active');
          }
        }}
        onBlur={() => {
          setIsTreeFocused(false);
          if (debug) {
            console.log('⚠️ Tree container lost focus - keyboard navigation disabled');
          }
        }}
        onClick={(e) => {
          // Don't interfere with focus management in filter mode
          if (filterMode) {
            return;
          }
          // Only steal focus if not clicking from an input field
          const activeElement = document.activeElement as HTMLElement;
          const isTypingInInput = activeElement && (
              activeElement.tagName === 'INPUT' || 
              activeElement.tagName === 'TEXTAREA' ||
              activeElement.getAttribute('contenteditable') === 'true' ||
              activeElement.closest('.MuiTextField-root') ||
              activeElement.closest('.MuiInputBase-root')
          );
          if (!isTypingInInput && treeContainerRef.current) {
            e.stopPropagation();
            treeContainerRef.current.focus();
            if (debug) {
              console.log('🎯 Tree clicked - focused for keyboard navigation');
            }
          }
        }}
        sx={{
          outline: 'none',
          position: 'relative',
          userSelect: 'none',
          // Visual indication when tree has focus (but not in fullscreen)
          border: !isFullscreen && isTreeFocused ? '2px solid' : (!isFullscreen ? '1px solid' : 'none'),
          borderColor: isTreeFocused ? 'primary.main' : 'divider',
          borderRadius: isFullscreen ? 0 : 1,
          transition: 'border-color 0.2s, border-width 0.2s',
          '&:focus': {
            outline: 'none',
          },
          '&:focus-visible': {
            outline: 'none', // Use our custom border instead
          }
        }}
      >
        <ScrollableArea 
          width={width} 
          height={height} 
          isFullscreen={isFullscreen}
          ref={scrollableAreaRef}
          role="region"
          aria-label="Tree content area - scrollable"
          sx={{
            // Mobile gets enhanced touch support
            ...(isMobileLayout(actualContainerWidth) && {
              touchAction: 'pan-x pan-y',
              userSelect: 'none',
            }),
          }}
        >
          {/* Handle empty states: no data, no layout nodes, or search with no results */}
          {!data || data.length === 0 || layout.nodes.length === 0 ? (
            <Box
              role="status"
              aria-live="polite"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                zIndex: 10,
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography 
                component="h2"
                variant="h5" 
                color="text.secondary"
                sx={{ 
                  fontWeight: 500,
                  mb: 1,
                  opacity: 0.7 
                }}
              >
                {!data || data.length === 0 ? 'No data available' : 'No record found'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ opacity: 0.5 }}
              >
                {!data || data.length === 0 ? 'Please provide data to display' : 'Try adjusting your search criteria'}
              </Typography>
            </Box>
          ) : isMobileLayout(actualContainerWidth) && !filterMode ? (
            /* Ultra-Simple Mobile Layout - Just use desktop layout with better spacing */
            <Box>              
              <TreeCanvas 
                sx={{ 
                  width: canvasWidth, 
                  height: canvasHeight,
                  transform: getCanvasTransform(),
                }}
              >
                {/* Render links */}
                <Links
                  links={virtualization ? visibleLinks : layout.links}
                  width={canvasWidth}
                  height={canvasHeight}
                  selectedNodeId={selectedNodeId}
                />
                {/* Render nodes */}
                {renderedNodes}
              </TreeCanvas>
            </Box>
          ) : (
            /* Desktop Layout - Normal TreeCanvas */
            <TreeCanvas 
              sx={{ 
                width: canvasWidth, 
                height: canvasHeight,
                transform: getCanvasTransform(),
              }}
            >
              {/* Render virtualized links - only for hierarchical mode */}
              {!filterMode && (
              <Links
                links={virtualization ? visibleLinks : layout.links}
                width={canvasWidth}
                height={canvasHeight}
                selectedNodeId={selectedNodeId}
              />
              )}
              {/* Render leg labels - only for hierarchical mode and Leg 3 only */}
              {!filterMode && layout.legLabels.map((legLabel: LegLabelData, _index: number) => {
                // Only render leg labels for Leg 3
                if (legLabel.legNumber !== 3) {
                  return null;
                }
                if (debug) {
                  console.log('🏷️ Rendering leg label', legLabel.legNumber, 'with props:', legLabel);
                }
                return (
                  <LegLabel
                    key={`leg-${legLabel.legNumber}`}
                    legNumber={legLabel.legNumber}
                    x={legLabel.x}
                    y={legLabel.y}
                    memberCount={legLabel.memberCount}
                    partnerCount={legLabel.partnerCount}
                  />
                );
              })}
              {/* Render virtualized nodes - massive performance gain */}
              {renderedNodes}
            </TreeCanvas>
          )}
        </ScrollableArea>
      </TreeContainer>
      {/* Internal Expand/Collapse All Button */}
      {!filterMode && ( // Only show in tree mode
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 32,
            zIndex: 1000,
          }}
        >
          <Button
            variant="contained"
            size="small"
            onClick={handleExpandCollapseAll}
            aria-label={expandedNodes.size > 0 ? `Collapse all ${expandedNodes.size} expanded nodes` : `Expand all nodes with children`}
            sx={{
              minWidth: 'auto',
              borderRadius: '50%',
              width: 48,
              height: 48,
              boxShadow: 2,
              fontSize: 24,
              '&:hover': {
                boxShadow: 4,
              },
            }}
            title={expandedNodes.size > 0 ? 'Collapse All' : 'Expand All'}
          >
            <span aria-hidden="true">{expandedNodes.size > 0 ? '⊟' : '⊞'}</span>
          </Button>
        </Box>
      )}
      {/* Fullscreen Toggle Button */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: !filterMode ? 96 : 32, // Position next to expand/collapse button in tree mode, or alone in filter mode
          zIndex: 1000,
        }}
      >
        <Button
          variant="contained"
          size="small"
          onClick={handleFullscreenToggle}
          aria-label={isFullscreen ? 'Exit fullscreen mode' : 'Enter fullscreen mode'}
          aria-pressed={isFullscreen}
          sx={{
            minWidth: 'auto',
            borderRadius: '50%',
            width: 48,
            height: 48,
            boxShadow: 2,
            fontSize: 20,
            '&:hover': {
              boxShadow: 4,
            },
          }}
          title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
        >
          <span aria-hidden="true">{isFullscreen ? '⛉' : '⛶'}</span>
        </Button>
      </Box>
      {/* Debug overlay with virtualization stats */}
      {debug && (
        <DebugOverlay
          metrics={{
            ...debugMetrics,
            // Add virtualization stats to debug overlay
            lastOperation: virtualization 
              ? `${filterMode ? 'Tiled' : 'Hierarchical'} Virtualized: ${totalVisible}/${layout.nodes.length} nodes (${virtualizationStats.renderingReduction}% reduction)`
              : debugMetrics.lastOperation
          }}
          onUpdate={updateDebugMetrics}
        />
      )}
    </Box>
  );
};